import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { WEBSOCKET_LIVE_DATA_REPORT_TOPIC_V2 } from '../const';
import { UserProfileService } from './user-profile.service';
import { WebsocketClient } from './websocket-client.service';
import { LiveDataReport } from '../../vehicles/model/live-data-report/live-data-report';
import { LiveDataReportType } from 'src/app/vehicles/model/live-data-report/live-data-report-type';
import { RouteControlReport } from 'src/app/vehicles/model/live-data-report/route-control-report';
import { AlcoholLockReport } from 'src/app/vehicles/model/live-data-report/alcohol-lock-report';
import { AlcoholLockEvent } from 'src/app/vehicles/model/live-data-report/alcohol-lock-event';
import { PanicButtonReport } from 'src/app/vehicles/model/live-data-report/panic-button-report';
import { VehicleOffCourseReport } from 'src/app/vehicles/model/live-data-report/vehicle-off-course-report';
import { StoreService } from 'src/app/store/service/store.service';
import { StoreContent } from 'src/app/store/model/store-model';
import { ALCOHOL_LOCK_STATE, ROUTE_CONTROL_STATE } from 'src/app/vehicles/model/gps-frame';
import { VehicleProperties } from 'src/app/vehicles/model/vehicle-properties';
import { RestService } from 'src/app/shared/service/rest.service';
import { HttpClient } from '@angular/common/http';
import { MergeData } from 'src/app/store/model/merge-data';
import { ToastService } from './toast.service';
import { AudioService } from './audio.service';
import { AudioType } from '../model/audio-type';
import { parseReport, parseReports } from 'src/app/vehicles/util/live-data-report-parser';
import { NOTIFICATIONS_STORE_NAME, NotificationType, Notification } from '../model/notification';
import { addLoggedUserIdToUrl, addServerPartAndLoggedUserIdToUrl } from '../../shared/util/string-util';
import { UserGroup } from '../model/user-profile';
import { UserTokenService } from './user-token.service';

@Injectable({
  providedIn: 'root'
})
export class LiveDataReportService extends RestService {

  private storageName = VehicleProperties.getName();
  private reportsSubscriptions: Subscription[] = [];
  private viewedReports: ViewedReports = {};

  constructor(http: HttpClient, private websocketClient: WebsocketClient, private userService: UserProfileService, private settings: UserSettingsService,
    private storeService: StoreService, private toast: ToastService, private audio: AudioService, private userTokenService: UserTokenService) {
    super(http);
  }

  init(): void {
    if (this.settings.isLiveDataReportEnabled()) {
      if (!this.storeService.isStorageTypeExists(this.storageName))
        this.storeService.addStorageType(this.storageName, new StoreContent<VehicleProperties>((properties: VehicleProperties) => properties.sideNumber));

      this.loadAllVehiclesReports();

      this.websocketClient.websocketConnection$
        .subscribe(connected => {
          if (connected)
            this.connectToLiveDataWebsocket();
          else
            this.clearReportsSubscriptions();
        });

      this.userTokenService.reloadGroups$.subscribe(groups => this.resubscribe(groups));
    }
  }

  private resubscribe(oldGroups: UserGroup[]): void {
    this.websocketClient.websocketConnection$
      .pipe(take(1)).subscribe(connected => {
        if (connected) {
          if (this.userService.isService()) {
            this.websocketClient.unsubscribe(WEBSOCKET_LIVE_DATA_REPORT_TOPIC_V2);
          } else {
            oldGroups.forEach(group => {
              this.websocketClient.unsubscribe(`${WEBSOCKET_LIVE_DATA_REPORT_TOPIC_V2}/${group.groupId}`);
            });
          }
          this.clearReportsSubscriptions();
          this.connectToLiveDataWebsocket();
        } else
          this.clearReportsSubscriptions();
      });
  }

  private clearReportsSubscriptions(): void {
    this.reportsSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.reportsSubscriptions = [];
  }

  private connectToLiveDataWebsocket(): void {
    if (this.userService.isService()) {
      this.reportsSubscriptions.push(this.handleData(this.websocketClient.subscribe(WEBSOCKET_LIVE_DATA_REPORT_TOPIC_V2)));
    } else {
      this.userService.getUserGroupsIds().subscribe(groupIds => {
        groupIds.forEach(groupId =>
          this.reportsSubscriptions.push(this.handleData(this.websocketClient.subscribe(`${WEBSOCKET_LIVE_DATA_REPORT_TOPIC_V2}/${groupId}`)))
        );
      });
    }
  }

  private handleData(observable: Observable<Record<string, any> | any>): Subscription {
    return observable
      .pipe(
        map((response: Record<string, any>) => parseReport(response)),
        filter(report => report != null)
      )
      .subscribe(report => {
        if (!this.shouldNotifyOnReport(report))
          return;

        const sideNumberParams = { sideNumber: report.sideNumber };

        switch (report.reportType) {
          case LiveDataReportType.ALCOHOL_LOCK:
            const alcoholReportData: AlcoholLockReport = report.reportData;
            const alcoholLockMergeData = this.createReportMergeData(report);
            this.storeService.merge(this.storageName, [alcoholLockMergeData], true);
            if (alcoholReportData.eventType == AlcoholLockEvent.ALCOHOL_TEST_NEGATIVE) {
              this.audio.playAudio(AudioType.ALARM);

              this.toast.showStickyDataToast('warning', null, 'alcoholTestFailedWarning', sideNumberParams);

              this.storeService.store<Notification>(NOTIFICATIONS_STORE_NAME, [
                { type: NotificationType.ALCOHOL_LOCK_NEGATIVE_RESULT, time: alcoholReportData.receiveTime, data: report }
              ]);
            }
            break;

          case LiveDataReportType.PANIC_BUTTON:
            const panicButtonReportData: PanicButtonReport = report.reportData;
            this.audio.playAudio(AudioType.ALARM);
            if (this.settings.isPanicButtonCameraPreviewEnabled() && panicButtonReportData.previewUrl) {
              const openCameraUrl = addServerPartAndLoggedUserIdToUrl(panicButtonReportData.previewUrl, this.userService, true);
              this.toast.showStickyDataToast(
                'panicButtonAlertTitle', sideNumberParams, 'panicButtonAlertText', sideNumberParams,
                { url: openCameraUrl }
              );
            } else {
              this.toast.showStickyDataToast('panicButtonAlertTitle', sideNumberParams, 'panicButtonUsedInVehicle', sideNumberParams);
            }

            this.storeService.store<Notification>(NOTIFICATIONS_STORE_NAME, [
              {
                type: NotificationType.PANIC_BUTTON, time: panicButtonReportData.receiveTime, data: report,
                url: addLoggedUserIdToUrl(panicButtonReportData.previewUrl, this.userService)
              }
            ]);
            break;

          case LiveDataReportType.ROUTE_CONTROL:
            const routeControlMergeData = this.createReportMergeData(report);
            this.storeService.merge(this.storageName, [routeControlMergeData], true);
            break;

          case LiveDataReportType.OFF_COURSE:
            const vehicleOffCourseReportData: VehicleOffCourseReport = report.reportData;

            this.toast.showStickyToast('warning', null, 'off_course_warn', sideNumberParams, 'warn');

            this.storeService.store<Notification>(NOTIFICATIONS_STORE_NAME, [
              { type: NotificationType.VEHICLE_OFF_COURSE, time: vehicleOffCourseReportData.receiveTime, data: report }
            ]);

            break;
        }
      });
  }

  private loadAllVehiclesReports(): void {
    this.doGet<LiveDataReport<any>[]>('api/vehicles/reports')
      .pipe(map(reports => parseReports(reports)))
      .subscribe(reports => {
        const mergeData = reports.map(report => this.createReportMergeData(report)).filter(data => data);
        this.storeService.merge(this.storageName, mergeData, true);
      });
  }

  private createReportMergeData(report: LiveDataReport<any>): MergeData | null {
    switch (report.reportType) {
      case LiveDataReportType.ALCOHOL_LOCK:
        const alcoholReportData: AlcoholLockReport = report.reportData;
        const alcoholLockReport = { key: report.sideNumber };
        alcoholLockReport[ALCOHOL_LOCK_STATE] = alcoholReportData.eventType;
        return { key: report.sideNumber, data: alcoholLockReport };
      case LiveDataReportType.ROUTE_CONTROL:
        const routeControlReportData: RouteControlReport = report.reportData;
        const routeControlReport = { key: report.sideNumber };
        routeControlReport[ROUTE_CONTROL_STATE] = routeControlReportData.eventType;
        return { key: report.sideNumber, data: routeControlReport };
      default:
        return null;
    }
  }

  private shouldNotifyOnReport(report: LiveDataReport<any>): boolean {
    if (this.viewedReports[report.reportType] && this.viewedReports[report.reportType][report.reportData.id])
      return false;

    if (!this.viewedReports[report.reportType])
      this.viewedReports[report.reportType] = {};
    this.viewedReports[report.reportType][report.reportData.id] = true;
    return true;
  }
}

interface ViewedReports {
  [key: string]: ViewedReportType;
}

interface ViewedReportType {
  [key: number]: boolean;
}
